import "../styles/edit.css";

import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { useDuplicateName, findDuplicateName } from "../hooks/useDuplicateName";
import { SizePicker } from "./SizePicker";
import { defaultSizeKeys } from "../utils/productSizes";
import { DuplicateNameNotice } from "./DuplicateNameNotice";
import firebase from "../../src/components/firebase";

const CLOUDINARY_CLOUD = 'dibfyycgl';
const CLOUDINARY_PRESET = 'handdrawn_clothes';

export const EditProduct = () => {
  const { prodId } = useParams();
  const db = firebase.firestore();
  const navigate = useNavigate();
  const [values, setValues] = useState({
    name: "",
    price: "",
    description: "",
    img: "",
    images: [""],
    video: "",
  });

  const [productTypes, setProductTypes] = useState([]);
  const [showAddType, setShowAddType] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRefs = useRef([]);
  const { match: duplicateName } = useDuplicateName(values.name, prodId);

  useEffect(() => {
    const loadProductTypes = async () => {
      try {
        const doc = await db.collection('Settings').doc('productTypes').get();
        if (doc.exists) {
          setProductTypes(doc.data().types || []);
        } else {
          const defaultTypes = [
            "мъже", "жени", "деца", "якета", "обувки", "пътечки",
            "коледа", "картички", "щампи", "чаши", "декупаж",
            "керамика", "семейство", "месечинки"
          ];
          await db.collection('Settings').doc('productTypes').set({ types: defaultTypes });
          setProductTypes(defaultTypes);
        }
      } catch (error) {
        console.error("Error loading product types:", error);
      }
    };
    loadProductTypes();
  }, [db]);

  useEffect(() => {
    const prodRef = db.collection("Products").doc(prodId);
    prodRef.get().then((doc) => {
      if (doc.exists) {
        const productData = doc.data();
        if (!productData.images && productData.img) {
          productData.images = [productData.img];
        } else if (!productData.images) {
          productData.images = [""];
        }
        // Products saved before the sizes field existed get the checkboxes
        // pre-filled with what the page was already guessing for them, so
        // opening and saving one changes nothing unless she edits it.
        if (!Array.isArray(productData.sizes)) {
          productData.sizes = defaultSizeKeys(productData);
        }
        setValues((prevValues) => ({ ...prevValues, ...productData }));
      }
    }).catch((error) => {
      console.log("Error getting document:", error);
    });
  }, [prodId, db]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues((state) => ({ ...state, [name]: value }));
  };

  const handleAddNewType = async () => {
    if (!newTypeName.trim()) {
      alert("Please enter a category name");
      return;
    }
    try {
      const updatedTypes = [...productTypes, newTypeName.trim()];
      await db.collection('Settings').doc('productTypes').set({ types: updatedTypes });
      setProductTypes(updatedTypes);
      setValues((state) => ({ ...state, type: newTypeName.trim() }));
      setNewTypeName("");
      setShowAddType(false);
    } catch (error) {
      console.error("Error adding new type:", error);
    }
  };

  const handleImageChange = (index, value) => {
    const newImages = [...values.images];
    newImages[index] = value;
    setValues((state) => ({ ...state, images: newImages }));
  };

  const handleFileUpload = async (index, file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert("Please upload images only (JPG, PNG, WEBP, GIF)");
      return;
    }

    setUploadProgress(prev => ({ ...prev, [index]: 0 }));

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_PRESET);

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(prev => ({ ...prev, [index]: pct }));
        }
      };

      xhr.onload = () => {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status === 200) {
          handleImageChange(index, data.secure_url);
        } else {
          alert('Upload failed. Please try again.');
        }
        setUploadProgress(prev => { const n = { ...prev }; delete n[index]; return n; });
      };

      xhr.onerror = () => {
        alert('Upload failed. Please try again.');
        setUploadProgress(prev => { const n = { ...prev }; delete n[index]; return n; });
      };

      xhr.send(formData);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadProgress(prev => { const n = { ...prev }; delete n[index]; return n; });
    }
  };

  const addImageField = () => {
    setValues((state) => ({ ...state, images: [...state.images, ""] }));
  };

  const removeImageField = (index) => {
    if (values.images.length > 1) {
      const newImages = values.images.filter((_, i) => i !== index);
      setValues((state) => ({ ...state, images: newImages }));
      setUploadProgress(prev => { const n = { ...prev }; delete n[index]; return n; });
    }
  };

  const generateCatalogNumber = () => {
    const now = new Date();
    const dateStr = now.getFullYear().toString() +
      (now.getMonth() + 1).toString().padStart(2, '0') +
      now.getDate().toString().padStart(2, '0');
    const randomStr = Math.random().toString(36).substring(2, 7).toUpperCase();
    setValues((state) => ({ ...state, catalogNumber: `CAT-${dateStr}-${randomStr}` }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (Object.keys(uploadProgress).length > 0) {
      alert("Please wait for uploads to finish");
      return;
    }

    const existing = await findDuplicateName(values.name, prodId);
    if (existing) {
      alert(
        `Вече съществува друг продукт с името „${existing.name}".\n\n` +
        `Google индексира само едната от две страници с еднакво заглавие. ` +
        `Добави нещо отличително към името, преди да запазиш.`
      );
      return;
    }

    const filteredImages = values.images.filter(img => img.trim() !== "");

    let catalogNumber = values.catalogNumber;
    if (!catalogNumber) {
      const now = new Date();
      const dateStr = now.getFullYear().toString() +
        (now.getMonth() + 1).toString().padStart(2, '0') +
        now.getDate().toString().padStart(2, '0');
      const randomStr = Math.random().toString(36).substring(2, 7).toUpperCase();
      catalogNumber = `CAT-${dateStr}-${randomStr}`;
    }

    const updatedValues = {
      ...values,
      img: filteredImages[0] || values.img || "",
      images: filteredImages,
      catalogNumber,
    };

    db.collection("Products").doc(prodId).update(updatedValues)
      .then(() => navigate(`/catalog/${prodId}`))
      .catch((error) => console.error("Error updating product:", error));
  };

  const uploadBtnStyle = {
    padding: "8px 14px",
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontSize: "13px",
  };

  return (
    <section id="edit-page" className="auth" style={{ marginTop: "40px" }}>
      <form id="edit" onSubmit={handleSubmit}>
        <div className="container" style={{ marginTop: "50px" }}>
          <h1>Edit Product</h1>

          <div style={{ display: "flex", flexDirection: "column" }}>
            <label htmlFor="name">Name:</label>
            <input
              type="text"
              id="name"
              name="name"
              value={values.name}
              onChange={handleInputChange}
              style={duplicateName ? { border: "2px solid #e0a800" } : undefined}
            />
            <DuplicateNameNotice match={duplicateName} />
          </div>

          <div style={{ display: "flex", flexDirection: "column", textAlign: "center" }}>
            <label htmlFor="price">Цена (€):</label>
            <input
              value={values.price}
              onChange={handleInputChange}
              type="number"
              id="price"
              name="price"
              min={0.01}
              step={0.01}
              placeholder={0}
              style={{ width: "230px" }}
            />

            <label htmlFor="type">Type:</label>
            <select value={values.type} onChange={handleInputChange} id="type" name="type">
              {productTypes.map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>

            <label style={{ marginTop: "12px" }}>За кои размери да пита продуктът:</label>
            <SizePicker
              value={values.sizes || []}
              onChange={(sizes) => setValues((state) => ({ ...state, sizes }))}
            />

            <button
              type="button"
              onClick={() => setShowAddType(!showAddType)}
              style={{ padding: "8px 20px", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", marginTop: "10px", marginBottom: "15px" }}
            >
              {showAddType ? "Cancel" : "+ Add new category"}
            </button>

            {showAddType && (
              <div style={{ marginBottom: "15px", padding: "15px", border: "2px solid #007bff", borderRadius: "4px", backgroundColor: "#f8f9fa" }}>
                <label htmlFor="newType">New category name:</label>
                <input
                  type="text"
                  id="newType"
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                  placeholder="Enter category name..."
                  style={{ marginBottom: "10px" }}
                />
                <button
                  type="button"
                  onClick={handleAddNewType}
                  style={{ padding: "8px 20px", backgroundColor: "#28a745", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
                >
                  Save category
                </button>
              </div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            <label htmlFor="description">Description:</label>
            <input type="text" id="description" name="description" value={values.description} onChange={handleInputChange} />
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            <label htmlFor="catalogNumber">Catalog number:</label>
            <div style={{ display: "flex", gap: "10px", marginBottom: "15px", alignItems: "center" }}>
              <input
                value={values.catalogNumber || ""}
                onChange={handleInputChange}
                type="text"
                id="catalogNumber"
                name="catalogNumber"
                placeholder="Leave blank for auto-generation"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={generateCatalogNumber}
                style={{ padding: "8px 20px", backgroundColor: "#6c757d", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", whiteSpace: "nowrap" }}
              >
                🔄 Generate
              </button>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            <label>Images:</label>
            {values.images.map((image, index) => (
              <div key={index} style={{ marginBottom: "14px" }}>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    ref={el => fileInputRefs.current[index] = el}
                    onChange={(e) => handleFileUpload(index, e.target.files[0])}
                  />
                  <button
                    type="button"
                    style={uploadBtnStyle}
                    onClick={() => fileInputRefs.current[index]?.click()}
                    disabled={uploadProgress[index] !== undefined}
                  >
                    📁 Upload
                  </button>
                  <input
                    value={image}
                    onChange={(e) => handleImageChange(index, e.target.value)}
                    type="text"
                    placeholder={`Image ${index + 1} — URL or upload file`}
                    style={{ flex: 1 }}
                  />
                  {values.images.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeImageField(index)}
                      style={{ padding: "5px 12px", backgroundColor: "#dc3545", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
                    >
                      ✕
                    </button>
                  )}
                </div>
                {uploadProgress[index] !== undefined && (
                  <div style={{ marginTop: "6px" }}>
                    <div style={{ height: "6px", backgroundColor: "#e9ecef", borderRadius: "3px", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${uploadProgress[index]}%`, backgroundColor: "#4CAF50", transition: "width 0.2s" }} />
                    </div>
                    <small style={{ color: "#666" }}>Uploading... {uploadProgress[index]}%</small>
                  </div>
                )}
                {image && uploadProgress[index] === undefined && (
                  <img
                    src={image}
                    alt={`preview ${index + 1}`}
                    style={{ marginTop: "6px", height: "60px", width: "60px", objectFit: "cover", borderRadius: "4px", border: "1px solid #ddd" }}
                  />
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addImageField}
              style={{ padding: "8px 20px", backgroundColor: "#28a745", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", marginBottom: "15px" }}
            >
              + Add image
            </button>
          </div>

          {/* Video upload */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ fontWeight: 600, display: "block", marginBottom: "6px" }}>Видео на продукта (по избор):</label>
            {values.video && (
              <video src={values.video} controls style={{ width: "100%", maxHeight: "160px", borderRadius: "6px", marginBottom: "8px" }} />
            )}
            <input
              type="file"
              accept="video/*"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                setUploadProgress(prev => ({ ...prev, video: 0 }));
                const formData = new FormData();
                formData.append('file', file);
                formData.append('upload_preset', CLOUDINARY_PRESET);
                const xhr = new XMLHttpRequest();
                xhr.open('POST', `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/video/upload`);
                xhr.upload.onprogress = (ev) => {
                  if (ev.lengthComputable)
                    setUploadProgress(prev => ({ ...prev, video: Math.round((ev.loaded / ev.total) * 100) }));
                };
                xhr.onload = () => {
                  const data = JSON.parse(xhr.responseText);
                  if (xhr.status === 200) {
                    setValues(prev => ({ ...prev, video: data.secure_url }));
                    setUploadProgress(prev => { const s = {...prev}; delete s.video; return s; });
                  }
                };
                xhr.send(formData);
              }}
              style={{ display: "block", marginBottom: "6px" }}
            />
            {uploadProgress.video !== undefined && (
              <div style={{ height: "6px", background: "#eee", borderRadius: "3px" }}>
                <div style={{ height: "100%", width: `${uploadProgress.video}%`, backgroundColor: "#4CAF50", transition: "width 0.2s", borderRadius: "3px" }} />
              </div>
            )}
          </div>

          <button type="submit" style={{ width: "80px" }}>Save</button>
        </div>
      </form>
    </section>
  );
};
